import type { ReactNode } from 'react';
import { hrefOf } from '../../router';
import { useLocale, useT } from '../../../messages';
import type { UiLocale } from '../../../messages';
import { sentenceOf } from './sentences';
import './activity.css';

/**
 * **A feed of sentences** — the shape the Activity tab and the Log share
 * (console redesign, phase B6; the mockup's `.feed`).
 *
 * One line per audit row: the minute, then "**Jose** turned on gift cards and
 * reports", then — closed until asked — the row as it was written. The
 * sentence is `sentences.ts`'s; this file only lays it out. A row that is only
 * somebody looking is set quieter, so the changes stand out when looks are
 * shown at all.
 *
 * **The raw record is one click away, and it is a list, not a string.** The old
 * screens printed the detail as `k=v k=v`, which is neither scannable nor
 * exact; here it is a key–value list, nested where the row nests (a `before`
 * and an `after` read as two little lists), with every value as it was stored.
 * Keys and values are the trail's own and are never translated — the raw
 * record is the thing a sentence can be checked against.
 */

export interface FeedEntry {
  id: string;
  at: string;
  actor: string;
  action: string;
  target: string;
  detail: Record<string, unknown>;
  /**
   * On the Log, the business the row is about (null for fleet-wide work),
   * named and linked to its page. Absent on a business's own Activity tab.
   */
  business?: { id: string; name: string | null } | null | undefined;
}

export function Feed({
  entries,
  showBusiness = false,
}: {
  entries: readonly FeedEntry[];
  showBusiness?: boolean;
}): ReactNode {
  const t = useT();
  const locale = useLocale();
  return (
    <ul className="afeed">
      {entries.map((entry) => {
        const s = sentenceOf(t, locale, entry);
        return (
          <li key={entry.id} className={`afeed__row${s.look ? ' is-look' : ''}`}>
            <FeedTime iso={entry.at} locale={locale} />
            <div className="afeed__main">
              {showBusiness ? (
                <div className="afeed__business">
                  {entry.business === null || entry.business === undefined ? (
                    <span className="muted">{t('log.allBusinesses')}</span>
                  ) : (
                    <a
                      href={hrefOf({
                        kind: 'business',
                        tenantId: entry.business.id,
                        tab: 'overview',
                      })}
                    >
                      {entry.business.name ?? entry.business.id}
                    </a>
                  )}
                </div>
              ) : null}
              <p className="afeed__sentence">
                <b>{s.who}</b> {s.what}
              </p>
              <details className="afeed__raw">
                <summary>{t('activity.raw')}</summary>
                <RawRecord entry={entry} />
              </details>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** "Sep 23 17:58" in the operator's own zone; the exact instant on hover. */
function FeedTime({ iso, locale }: { iso: string; locale: UiLocale }): ReactNode {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return <time className="afeed__time">{iso}</time>;
  return (
    <time className="afeed__time" dateTime={iso} title={iso}>
      {new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).format(parsed)}
    </time>
  );
}

/** The row as it was written: action, actor, target, instant, and the detail as a list. */
export function RawRecord({ entry }: { entry: FeedEntry }): ReactNode {
  const t = useT();
  return (
    <dl className="kvlist">
      <dt>{t('activity.raw.action')}</dt>
      <dd>
        <code>{entry.action}</code>
      </dd>
      <dt>{t('activity.raw.actor')}</dt>
      <dd>
        <code>{entry.actor}</code>
      </dd>
      <dt>{t('activity.raw.target')}</dt>
      <dd>
        {entry.target === '' ? (
          <span className="muted">{t('activity.raw.none')}</span>
        ) : (
          <code>{entry.target}</code>
        )}
      </dd>
      <dt>{t('activity.raw.at')}</dt>
      <dd>
        <code>{entry.at}</code>
      </dd>
      <dt>{t('activity.raw.detail')}</dt>
      <dd>
        {Object.keys(entry.detail).length === 0 ? (
          <span className="muted">{t('activity.raw.none')}</span>
        ) : (
          <DetailList value={entry.detail} />
        )}
      </dd>
    </dl>
  );
}

/**
 * One value of a detail. Objects become a nested list, lists of plain values a
 * comma list, lists of records a numbered list of little lists; a plain value
 * is printed as stored (`null` included — it means something different from
 * absent).
 */
function DetailList({ value }: { value: Record<string, unknown> }): ReactNode {
  return (
    <dl className="kvlist kvlist--nested">
      {Object.entries(value).map(([key, v]) => (
        <DetailPair key={key} name={key} value={v} />
      ))}
    </dl>
  );
}

function DetailPair({ name, value }: { name: string; value: unknown }): ReactNode {
  return (
    <>
      <dt>
        <code>{name}</code>
      </dt>
      <dd>
        <DetailValue value={value} />
      </dd>
    </>
  );
}

function DetailValue({ value }: { value: unknown }): ReactNode {
  if (value === null || value === undefined) return <code className="muted">null</code>;
  if (Array.isArray(value)) {
    if (value.length === 0) return <code className="muted">[]</code>;
    if (value.every((v) => v === null || typeof v !== 'object')) {
      return <code>{value.map((v) => String(v)).join(', ')}</code>;
    }
    return (
      <ol className="kvlist__items">
        {value.map((v, i) => (
          // The list’s own order is its identity: a record in it has no id.
          <li key={i}>
            <DetailValue value={v} />
          </li>
        ))}
      </ol>
    );
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return Object.keys(record).length === 0 ? (
      <code className="muted">{'{}'}</code>
    ) : (
      <DetailList value={record} />
    );
  }
  return <code>{String(value as string | number | boolean)}</code>;
}
